import { resolveAppLanguage } from '../../../shared/config';

const TIMESTAMP_CONNECTOR_BY_LANGUAGE = {
  en: 'at',
  es: 'a las',
  pt: 'às',
} as const;

const TIMESTAMP_LOCALE_BY_LANGUAGE = {
  en: 'en-GB',
  es: 'es-AR',
  pt: 'pt-BR',
} as const;

export type TipsStatementSectionTitleKey =
  | 'tipsStatement.sectionToday'
  | 'tipsStatement.sectionYesterday';

type TipsStatementTimeContext = {
  now?: Date;
  timeZone?: string;
};

type TipsStatementCalendarDate = {
  day: string;
  month: string;
  year: string;
};

type TipsStatementSectionDescriptor = {
  id: string;
  title?: string;
  titleKey?: TipsStatementSectionTitleKey;
};

function readPart(
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes,
): string {
  const value = parts.find(part => part.type === type)?.value;

  if (value) {
    return value;
  }

  return '';
}

function resolveTipsStatementLocale(language: string): string {
  return TIMESTAMP_LOCALE_BY_LANGUAGE[resolveAppLanguage(language)];
}

function createDateFormatter(
  language: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(resolveTipsStatementLocale(language), options);
}

function readCalendarDate(
  language: string,
  date: Date,
  timeZone?: string,
): TipsStatementCalendarDate {
  const formatter = createDateFormatter(language, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone,
  });
  const parts = formatter.formatToParts(date);

  return {
    day: readPart(parts, 'day'),
    month: readPart(parts, 'month'),
    year: readPart(parts, 'year'),
  };
}

function createCalendarDateId({
  day,
  month,
  year,
}: TipsStatementCalendarDate): string {
  return `${year}-${month}-${day}`;
}

function capitalizeLabel(value: string): string {
  if (value.length === 0) {
    return value;
  }

  return value[0].toUpperCase() + value.slice(1);
}

export function formatTipsStatementTimestamp(
  language: string,
  isoTimestamp: string,
  { timeZone }: TipsStatementTimeContext = {},
): string {
  const timestamp = new Date(isoTimestamp);

  if (Number.isNaN(timestamp.getTime())) {
    return '';
  }

  const appLanguage = resolveAppLanguage(language);
  const formatter = createDateFormatter(language, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone,
  });
  const parts = formatter.formatToParts(timestamp);
  const day = readPart(parts, 'day');
  const month = readPart(parts, 'month');
  const year = readPart(parts, 'year');
  const hour = readPart(parts, 'hour');
  const minute = readPart(parts, 'minute');

  return `${day}/${month}/${year} ${TIMESTAMP_CONNECTOR_BY_LANGUAGE[appLanguage]} ${hour}:${minute}`;
}

export function resolveTipsStatementSectionDescriptor(
  language: string,
  isoTimestamp: string,
  { now = new Date(), timeZone }: TipsStatementTimeContext = {},
): TipsStatementSectionDescriptor {
  const timestamp = new Date(isoTimestamp);

  if (Number.isNaN(timestamp.getTime())) {
    return {
      id: isoTimestamp,
      title: '',
    };
  }

  const sectionDate = readCalendarDate(language, timestamp, timeZone);
  const sectionDateId = createCalendarDateId(sectionDate);
  const currentDateId = createCalendarDateId(
    readCalendarDate(language, now, timeZone),
  );

  if (sectionDateId === currentDateId) {
    return {
      id: sectionDateId,
      titleKey: 'tipsStatement.sectionToday',
    };
  }

  const yesterday = new Date(now.getTime());
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  const yesterdayDateId = createCalendarDateId(
    readCalendarDate(language, yesterday, timeZone),
  );

  if (sectionDateId === yesterdayDateId) {
    return {
      id: sectionDateId,
      titleKey: 'tipsStatement.sectionYesterday',
    };
  }

  const monthFormatter = createDateFormatter(language, {
    month: 'long',
    timeZone,
  });
  const monthTitle = capitalizeLabel(monthFormatter.format(timestamp));
  const currentYear = readCalendarDate(language, now, timeZone).year;
  const title =
    sectionDate.year === currentYear
      ? monthTitle
      : `${monthTitle} ${sectionDate.year}`;

  return {
    id: `${sectionDate.year}-${sectionDate.month}`,
    title,
  };
}
